import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Link,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react'
import type { FormikErrors } from 'formik'
import { Field, FieldArray, FieldProps, Form, Formik } from 'formik'
import { Base64 } from 'js-base64'
import { isEqual } from 'lodash'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinFill } from 'react-icons/ri'
import { useHistory, useParams } from 'react-router-dom'
import { components, MultiValueGenericProps, OptionProps } from 'react-select'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { IKReactSelect } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import {
  CmFieldTypeEnum,
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT,
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP,
  MAX_CM_TEXT_LENGTH,
} from '../../constants'
import { useDebounce } from '../../hooks/useDebounce'
import useToast from '../../hooks/useToast'
import type {
  FolderDetailsResponse,
  GetPathPolicyResponse,
  PathPolicyErrorResponse,
} from '../../interfaces'
import { MainContentWrapper } from '../../layouts/Base/MainContent'
import { UserContext } from '../../providers'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import FormDirtyPrompt from '../FormDirtyPrompt'
import PermissionGuard from '../PermissionGuard'
import CodeEditor from './CodeEditor'
import DefaultValueFormComponent from './DefaultValueFormComponent'
import { DeletePathPolicyModal } from './DeletePathPolicyModal'
import PathPolicyHeader from './PathPolicyHeader'
import SelectCMFModal from './SelectCMFModal'
import { PathPolicySideMenuRef } from './SideMenu'

interface FolderOption {
  label: string
  value: string
}

export interface PathPolicyFormValues {
  name: string
  description: string
  folderPaths: FolderOption[]
  validateFunctionEnabled: boolean
  validateFunction: string
  uploadFunctionEnabled: boolean
  uploadFunction: string
  customMetadataFields: Array<{
    isSelected: boolean
    name: string
    schema: {
      type: CmFieldTypeEnum
      selectOptions?: Array<string | number | boolean>
      defaultValue?:
        | string
        | number
        | boolean
        | Array<string | number | boolean>
      isValueRequired?: boolean
      readOnly?: boolean
      minValue?: string | number
      maxValue?: string | number
      minLength?: number
      maxLength?: number
    }
  }>
}

const formatDate = (date: string) => moment(date).format('MMMM Do YYYY')

interface Props {
  sideMenuRef: React.RefObject<PathPolicySideMenuRef>
}

function getLastFolder(fullPath) {
  const segments = fullPath.split('/').filter(Boolean)
  return segments.length ? segments.pop() : ''
}

const FolderOption = (props: OptionProps<any>) => {
  const { data, children } = props

  return (
    <components.Option {...props}>
      <Flex w="full" align="center" justify="space-between">
        <Box>{children}</Box>

        <Tooltip label={data.value} hasArrow>
          <Box
            ml={4}
            color="gray.500"
            fontSize="xs"
            maxW="50%"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {data.value}
          </Box>
        </Tooltip>
      </Flex>
    </components.Option>
  )
}

const MultiValueLabelLink = (props: MultiValueGenericProps<any>) => {
  const { data } = props

  return (
    <components.MultiValueLabel {...props}>
      <Link
        as="a"
        href={`/dashboard/media-library/${Base64.encode(data.value)}`}
        target="_blank"
        rel="noopener noreferrer"
        _hover={{ textDecoration: 'underline' }}
        onClick={e => e.stopPropagation()}
      >
        {data.label}
      </Link>
    </components.MultiValueLabel>
  )
}

const PathPolicyInput = props => (
  <components.Input {...props} className="path-policy-folderpaths" />
)

export const PathPolicyForm: React.FC<Props> = ({ sideMenuRef }) => {
  const { t } = useTranslation()

  const history = useHistory()
  const { pathPolicyId } = useParams<{ pathPolicyId: string }>()
  const [duplicateFolderPaths, setDuplicateFolderPaths] = useState<string[]>([])

  const toast = useToast()
  const { user } = useContext(UserContext)
  const { cmFields } = useContext(CustomMetadataContext)

  const {
    get: getPolicy,
    loading: loadingPathPolicy,
    request: pathPolicyRequest,
    response: pathPolicyResponse,
  } = useFetch<GetPathPolicyResponse>(
    `/api/clients/${user?.clientNumber}/path-policy/${pathPolicyId}`,
    { cachePolicy: CachePolicies.NO_CACHE },
  )
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [formError, setFormError] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const [folderOptions, setFolderOptions] = useState<FolderOption[]>([])

  const validationSchema = Yup.object({
    name: Yup.string().trim().required('Name is required'),
    description: Yup.string()
      .trim()
      .max(150, 'Description can have at most 150 characters')
      .required('Description is required'),
    uploadFunction: Yup.string().when('uploadFunctionEnabled', {
      is: true,
      then: (schema) => schema.trim().min(1, 'Upload Function is required when enabled'),
      otherwise: (schema) => schema,
    }),
    validateFunction: Yup.string().when('validateFunctionEnabled', {
      is: true,
      then: (schema) => schema.trim().min(1, 'Validate Function is required when enabled'),
      otherwise: (schema) => schema,
    }),
  })

  const initialValues: PathPolicyFormValues = {
    name: '',
    description: '',
    folderPaths: [],
    validateFunctionEnabled: false,
    validateFunction: 'function handler(operation, payload, user) {\n}',
    uploadFunction:
      'function handler(operation, payload, user) {\n      return payload;\n}',
    uploadFunctionEnabled: false,
    customMetadataFields: [],
  }
  const [formValues, setFormValues] =
    useState<PathPolicyFormValues>(initialValues)

  const {
    isOpen: isOpenDeletePathPolicy,
    onOpen: onOpenDeletePathPolicy,
    onClose: onCloseDeletePathPolicy,
  } = useDisclosure()

  const {
    isOpen: isOpenSelectCMFModal,
    onOpen: onOpenSelectCMFModal,
    onClose: onCloseSelectCMFModal,
  } = useDisclosure()

  const debouncedSearch = useDebounce(search, 500)

  useEffect(() => {
    if (formError) {
      toast({
        status: 'error',
        isClosable: true,
        duration: duplicateFolderPaths.length > 0 ? 10000 : 5000,
        position: 'top-right',
        variant: 'outline',
        description: (
          <Box>
            <Text>{formError}</Text>
            {duplicateFolderPaths.length > 0 && (
              <>
                <Text mt={2}>Duplicate folder paths:</Text>
                <UnorderedList ml={4} mt={1} spacing={1}>
                  {duplicateFolderPaths.map(p => (
                    <ListItem key={p}>{p}</ListItem>
                  ))}
                </UnorderedList>
              </>
            )}
          </Box>
        ),
      })
    }
  }, [formError, duplicateFolderPaths])
  

  const addNewPolicy = async ({
    values,
  }: {
    values: PathPolicyFormValues
  }): Promise<void> => {
    setFormError('')

    const resp = (await request.post(
      `/api/clients/${user?.clientNumber}/path-policy`,
      {
        name: values.name.trim(),
        description: values.description,
        folderPaths: values.folderPaths.map(folder => folder.value),
        validateFunctionEnabled: values.validateFunctionEnabled,
        validateFunction: values.validateFunction.trim(),
        uploadFunctionEnabled: values.uploadFunctionEnabled,
        uploadFunction: values.uploadFunction.trim(),
        customMetadataFields: values.customMetadataFields
          .filter(cmf => cmf.isSelected)
          .map(cmf => ({
            name: cmf.name,
            type: cmf.schema.type,
            defaultValue: cmf.schema.defaultValue,
            isValueRequired: cmf.schema.isValueRequired,
            readOnly: cmf.schema.readOnly,
          })),
      },
    )) as GetPathPolicyResponse | PathPolicyErrorResponse

    if (request.error) {
      const { message, duplicateFolderPaths: dups = [] } =
        resp as PathPolicyErrorResponse & { duplicateFolderPaths?: string[] }

      setDuplicateFolderPaths(dups)
      setFormError(
        message ??
          t(
            'error_in_creating_path_policy',
            'An unknown error occurred while creating the new path policy.',
          ),
      )
      return
    }

    setDuplicateFolderPaths([])

    toast({
      description: t(
        'new_path_policy_created',
        'Path policy added successfully',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })

    history.push(`/path-policy/${(resp as GetPathPolicyResponse).pathPolicyId}`)
    await sideMenuRef?.current?.getPathPolicies()
  }

  const updatePolicy = async ({
    pathPolicyId,
    values,
  }: {
    pathPolicyId: string
    values: PathPolicyFormValues
  }): Promise<void> => {
    setFormError('')

    const changedValues = Object.entries(values).reduce<Record<string, any>>(
      (acc, [key, val]) => {
        if (!isEqual(val, (formValues as any)[key])) {
          acc[key] = val
        }
        return acc
      },
      {},
    )

    if ('name' in changedValues && typeof changedValues.name === 'string') {
      changedValues.name = changedValues.name.trim()
    }

    if (
      'validateFunction' in changedValues &&
      typeof changedValues.validateFunction === 'string'
    ) {
      changedValues.validateFunction = changedValues.validateFunction.trim()
    }

    if (
      'uploadFunction' in changedValues &&
      typeof changedValues.uploadFunction === 'string'
    ) {
      changedValues.uploadFunction = changedValues.uploadFunction.trim()
    }

    if ('customMetadataFields' in changedValues) {
      changedValues.customMetadataFields = values.customMetadataFields
        .filter(cmf => cmf.isSelected)
        .map(cmf => ({
          name: cmf.name,
          type: cmf.schema.type,
          defaultValue: cmf.schema.defaultValue,
          isValueRequired: cmf.schema.isValueRequired,
          readOnly: cmf.schema.readOnly,
        }))
    }

    if ('folderPaths' in changedValues) {
      changedValues.folderPaths = values.folderPaths.map(folder => folder.value)
    }

    const resp = (await request.put(
      `/api/clients/${user?.clientNumber}/path-policy/${pathPolicyId}`,
      changedValues,
    )) as GetPathPolicyResponse | PathPolicyErrorResponse

    if (request.error) {
      const { message, duplicateFolderPaths: dups = [] } =
        resp as PathPolicyErrorResponse & { duplicateFolderPaths?: string[] }

      setDuplicateFolderPaths(dups)
      setFormError(
        message ??
          t(
            'error_in_updating_path_policy',
            'An unknown error occurred while updating the path policy.',
          ),
      )
      return
    }
    setDuplicateFolderPaths([])

    toast({
      description: t('path_policy_updated', 'Path policy updated successfully'),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })

    setFormValues(values)
    await sideMenuRef?.current?.getPathPolicies()
  }

  const deletePathPolicy = async (): Promise<void> => {
    if (user?.clientNumber) {
      const resp = (await request.del(
        `/api/clients/${user.clientNumber}/path-policy/${pathPolicyId}`,
      )) as { pathPolicyId: string } | PathPolicyErrorResponse

      if (request.error) {
        const message = (resp as PathPolicyErrorResponse)?.message
        setFormError(
          message ??
            t(
              'unknown_error_in_deleting_path_policy',
              'An unknown error occurred while deleting the path policy.',
            ),
        )
        return
      }

      setFormError('')
      toast({
        description: t(
          'path_policy_deleted_successfully',
          'Path policy deleted successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })

      await sideMenuRef?.current?.getPathPolicies()
      history.push('/path-policy')
    }
  }

  const getPathpolicy = async (): Promise<void> => {
    if (!pathPolicyId) {
      return
    }

    setFormError('')

    if (pathPolicyId === 'new') {
      setFormValues(initialValues)

      const mappedCmfs = cmFields.map(field => ({
        isSelected: false,
        name: field.name,
        schema: {
          type: field.schema.type,
          selectOptions: field.schema.selectOptions,
          defaultValue: field.schema.defaultValue,
          isValueRequired: field.schema.isValueRequired,
          readOnly: field.schema.readOnly ?? false,
        },
      }))

      setFormValues({
        ...initialValues,
        customMetadataFields: mappedCmfs,
      })
    } else {
      const resp = await getPolicy()

      if (pathPolicyRequest.error) {
        setFormError(
          t(
            'error_in_getting_path_policy',
            'Error in getting path policy details.',
          ),
        )
      } else {
        setFormValues({
          name: resp.name,
          description: resp.description,
          folderPaths: resp.folderPaths.map(f => {
            return {
              label: getLastFolder(f),
              value: f,
            }
          }),
          validateFunction: resp.validateFunction,
          validateFunctionEnabled: resp.validateFunctionEnabled,
          uploadFunction: resp.uploadFunction,
          uploadFunctionEnabled: resp.uploadFunctionEnabled,
          customMetadataFields: resp.customMetadataFields.map(field => ({
            isSelected: field.isSelected,
            name: field.name,
            schema: {
              type: field.schema.type,
              selectOptions: field.schema.selectOptions,
              isValueRequired: field.schema.isValueRequired ?? false,
              defaultValue: field.schema.defaultValue,
              readOnly: field.schema.readOnly ?? false,
            },
          })),
        })
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams({
      skip: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP.toString(),
      limit: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT.toString(),
      sort: 'DESC_RELEVANCE',
      searchQuery: `(type = folder AND (name HAS "${debouncedSearch}"))`,
    }).toString()

    let cancelled = false
    fetch(`/api/v2/files?${params}`)
      .then(res => (res.ok ? res.json() : []))
      .then((data: FolderDetailsResponse[]) => {
        if (!cancelled) {
          setFolderOptions(
            data.map(f => ({ label: f.name, value: f.folderPath })),
          )
        }
      })
      .catch(() => {
        if (!cancelled) setFolderOptions([])
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch])

  useEffect(() => {
    void getPathpolicy()
  }, [user, pathPolicyId, cmFields])

  const validateCustomMetadata = useCallback((values: PathPolicyFormValues) => {
    const errors: FormikErrors<PathPolicyFormValues> = {}

    values.customMetadataFields.forEach((cm, idx) => {
      if (!cm.isSelected) return
      const errObj: any = {}

      if (
        [CmFieldTypeEnum.SingleSelect, CmFieldTypeEnum.MultiSelect].includes(
          cm.schema.type,
        )
      ) {
        const opts = cm.schema.selectOptions ?? []
        if (!opts.length) {
          errObj.selectOptions = t(
            'create_at_least_one_select_option',
            'Create at least one select option',
          )
        } else if (new Set(opts).size !== opts.length) {
          errObj.selectOptions = t(
            'select_options_duplicates error',
            'Must not have duplicate values',
          )
        } else if (
          opts.some(o => typeof o === 'string' && o.length > MAX_CM_TEXT_LENGTH)
        ) {
          errObj.selectOptions = t('select_options_value_max_length_error', {
            maxLength: MAX_CM_TEXT_LENGTH,
          })
        }
      }

      if (
        [CmFieldTypeEnum.Text, CmFieldTypeEnum.Textarea].includes(
          cm.schema.type,
        ) &&
        typeof cm.schema.defaultValue === 'string'
      ) {
        const dv = cm.schema.defaultValue
        if (dv && dv.trim() === '') {
          errObj.defaultValue = t(
            'value_cannot_be_empty_string',
            'Must be non empty string with at least one non-space character.',
          )
        }
        if (
          cm.schema.minLength !== undefined &&
          dv.length < cm.schema.minLength
        ) {
          errObj.defaultValue = t('custom_metadata_value_min_length_error', {
            minLength: cm.schema.minLength,
          })
        }
        if (
          cm.schema.maxLength !== undefined &&
          dv.length > cm.schema.maxLength
        ) {
          errObj.defaultValue = t('custom_metadata_value_max_length_error', {
            maxLength: cm.schema.maxLength,
          })
        }
        if (dv.length > MAX_CM_TEXT_LENGTH) {
          errObj.defaultValue = t('custom_metadata_value_max_length_error', {
            maxLength: MAX_CM_TEXT_LENGTH,
          })
        }
        if (
          cm.schema.minLength !== undefined &&
          cm.schema.maxLength !== undefined &&
          cm.schema.minLength > cm.schema.maxLength
        ) {
          errObj.minLength = t(
            'min_length_max_length_discrepancy_error',
            'Minimum length must be less than or equal to maximum length',
          )
        }
      }

      if (cm.schema.type === CmFieldTypeEnum.Number) {
        const dv = cm.schema.defaultValue as number | undefined
        if (
          dv !== undefined &&
          cm.schema.minValue !== undefined &&
          dv < cm.schema.minValue
        ) {
          errObj.defaultValue = t('default_value_min_value_error', {
            minValue: cm.schema.minValue,
          })
        }
        if (
          dv !== undefined &&
          cm.schema.maxValue !== undefined &&
          dv > cm.schema.maxValue
        ) {
          errObj.defaultValue = t('default_value_max_value_error', {
            maxValue: cm.schema.maxValue,
          })
        }
        if (
          cm.schema.minValue !== undefined &&
          cm.schema.maxValue !== undefined &&
          cm.schema.minValue > cm.schema.maxValue
        ) {
          errObj.minValue = t(
            'min_value_max_value_discrepancy_error',
            'Minimum value must be less than or equal to maximum value',
          )
        }
      }

      if (cm.schema.type === CmFieldTypeEnum.Date) {
        const dv = cm.schema.defaultValue as string | undefined
        if (
          dv &&
          cm.schema.minValue &&
          new Date(dv) < new Date(cm.schema.minValue)
        ) {
          errObj.defaultValue = t('default_value_min_value_error', {
            minValue: formatDate(cm.schema.minValue as string),
          })
        }
        if (
          dv &&
          cm.schema.maxValue &&
          new Date(dv) > new Date(cm.schema.maxValue)
        ) {
          errObj.defaultValue = t('default_value_max_value_error', {
            maxValue: formatDate(cm.schema.maxValue as string),
          })
        }
        if (
          cm.schema.minValue &&
          cm.schema.maxValue &&
          new Date(cm.schema.minValue) > new Date(cm.schema.maxValue)
        ) {
          errObj.minValue = t(
            'min_date_max_date_discrepancy_error',
            'Minimum date must be earlier than or equal to maximum date',
          )
        }
      }

      if (Object.keys(errObj).length) {
        if (!errors.customMetadataFields) errors.customMetadataFields = []
        ;(errors.customMetadataFields as any)[idx] = { schema: errObj }
      }
    })

    return errors
  }, [])

  return (
    <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
      {(hasPermission: boolean) => (
        <>
          {!loadingPathPolicy ? (
            <Formik
              enableReinitialize
              initialValues={formValues}
              validationSchema={validationSchema}
              validate={validateCustomMetadata}
              onSubmit={async (values: PathPolicyFormValues) => {
                if (pathPolicyId && pathPolicyId !== 'new') {
                  await updatePolicy({ pathPolicyId, values })
                } else {
                  await addNewPolicy({ values })
                }
              }}
            >
              {({
                values,
                setFieldValue,
                dirty,
                isSubmitting,
                errors,
                touched,
                resetForm,
              }) => {
                const selectionFlags = values.customMetadataFields.map(
                  cmf => cmf.isSelected,
                )

                const handleAddCmfs = (idxs: number[]) => {
                  idxs.forEach(i =>
                    setFieldValue(`customMetadataFields.${i}.isSelected`, true),
                  )
                }

                const confirmDelete = (idx: number) =>
                  setFieldValue(`customMetadataFields.${idx}.isSelected`, false)
                return (
                  <Form>
                    <FormDirtyPrompt isDeleting={isOpenDeletePathPolicy} />
                    <PathPolicyHeader
                      {...{
                        pathPolicyId,
                        values,
                        dirty,
                        errors,
                        isSubmitting,
                        onOpenDeletePathPolicy,
                        setFormError,
                        resetForm,
                      }}
                    />
                    <MainContentWrapper>
                      <Stack direction={['column']} spacing="2.5em">
                        <Alert status="info" mt="1em">
                          <AlertIcon />
                          <AlertDescription>
                            {t(
                              'path_policy_info_form',
                              'Configure a path policy by selecting custom metadata fields and defining upload and validation functions to enforce rules at the folder path.',
                            )}
                            <br />
                            <Trans t={t} i18nKey="learn_more_from_the_docs">
                              <Link
                                fontSize="1em"
                                isExternal
                                href="https://imagekit.io/docs/dam/path-policy"
                              >
                                Learn
                              </Link>{' '}
                              from the docs.
                            </Trans>
                          </AlertDescription>
                        </Alert>
                        <Field name="name">
                          {({ field, meta }) => (
                            <FormControl
                              isRequired
                              isInvalid={Boolean(errors.name && touched.name)}
                            >
                              <FormLabel>{t('name', 'Name')}</FormLabel>
                              <Input
                                {...field}
                                placeholder={t(
                                  'enter_policy_name',
                                  'Enter policy name',
                                )}
                                isDisabled={!hasPermission}
                                maxLength={150}
                              />
                              <FormErrorMessage>{meta.error}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="description">
                          {({ field, meta }) => (
                            <FormControl
                              isRequired
                              isInvalid={Boolean(meta.error && meta.touched)}
                            >
                              <FormLabel>
                                {t('description', 'Description')}
                              </FormLabel>
                              <Input
                                {...field}
                                placeholder="Description"
                                isDisabled={!hasPermission}
                                maxLength={150}
                              />
                              <FormErrorMessage>{meta.error}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="folderPaths">
                          {({
                            field,
                            form,
                          }: FieldProps<
                            FolderOption[],
                            PathPolicyFormValues
                          >) => (
                            <FormControl
                              isInvalid={Boolean(
                                errors.folderPaths && touched.folderPaths,
                              )}
                            >
                              <FormLabel>
                                {t('folder_paths', 'Folder paths')}
                              </FormLabel>

                              <IKReactSelect
                                {...field}
                                isMulti
                                variant="compact"
                                defaultValue={field.value}
                                options={folderOptions}
                                inputValue={search}
                                onInputChange={val => setSearch(val)}
                                onChange={opts =>
                                  form.setFieldValue(field.name, opts || [])
                                }
                                value={field.value}
                                placeholder={t(
                                  'type_to_search_folders',
                                  'Type to search folders',
                                )}
                                isDisabled={!hasPermission}
                                components={{
                                  Option: FolderOption,
                                  MultiValueLabel: MultiValueLabelLink,
                                  Input: PathPolicyInput,
                                }}
                              />

                              {errors.folderPaths && touched.folderPaths && (
                                <Box color="red.500" mt="1">
                                  {errors.folderPaths as string}
                                </Box>
                              )}
                            </FormControl>
                          )}
                        </Field>

                        <Field name="validateFunctionEnabled">
                          {({ field }) => (
                            <FormControl>
                              <Checkbox
                                {...field}
                                isChecked={field.value}
                                isDisabled={!hasPermission}
                              >
                                {t(
                                  'enable_update_function',
                                  'Enable validate function',
                                )}
                              </Checkbox>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="validateFunction">
                          {({ field, form, meta }) => (
                            <FormControl isInvalid={Boolean(meta?.error)}>
                              <FormLabel>
                                {t(
                                  'validate_function',
                                  'Validate function (JavaScript)',
                                )}
                              </FormLabel>
                              <CodeEditor
                                value={field.value}
                                onChange={val =>
                                  form.setFieldValue(field.name, val)
                                }
                                readOnly={!hasPermission}
                                onSyntaxErrorChange={has => {
                                  if (has) {
                                    form.setFieldError(
                                      field.name,
                                      "Fix JavaScript syntax errors before saving.",
                                    )
                                  } else {
                                    form.setFieldError(field.name, undefined)
                                  }
                                }}
                              />
                              <FormErrorMessage>{meta?.error}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="uploadFunctionEnabled">
                          {({ field }) => (
                            <FormControl>
                              <Checkbox
                                {...field}
                                isChecked={field.value}
                                isDisabled={!hasPermission}
                              >
                                {t(
                                  'enable_upload_function',
                                  'Enable upload function',
                                )}
                              </Checkbox>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="uploadFunction">
                          {({ field, form, meta }) => (
                            <FormControl isInvalid={Boolean(meta?.error)}>
                              <FormLabel>
                                {t(
                                  'upload_function',
                                  'Upload function (JavaScript)',
                                )}
                              </FormLabel>
                              <CodeEditor
                                value={field.value}
                                onChange={val =>
                                  form.setFieldValue(field.name, val)
                                }
                                readOnly={!hasPermission}
                                onSyntaxErrorChange={has => {
                                  if (has) {
                                    form.setFieldError(
                                      field.name,
                                      "Fix JavaScript syntax errors before saving.",
                                    )
                                  } else {
                                    form.setFieldError(field.name, undefined)
                                  }
                                }}
                              />
                              <FormErrorMessage>{meta?.error}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <FieldArray name="customMetadataFields">
                          {() => (
                            <>
                              <Flex direction="column" align="flex-start">
                                <FormLabel>
                                  {t(
                                    'custom_metadata_fields',
                                    'Custom metadata fields',
                                  )}
                                </FormLabel>
                                <Button
                                  variant="link"
                                  color="appClearBlue"
                                  onClick={onOpenSelectCMFModal}
                                >
                                  +{' '}
                                  {t(
                                    'select_custom_metadata_fields',
                                    'Select custom metadata fields',
                                  )}
                                </Button>
                              </Flex>

                              <Table>
                                <Thead bg="appPaleGreyTwo">
                                  <Tr>
                                    <Th>{t('name', 'Name')}</Th>
                                    <Th>{t('type', 'Type')}</Th>
                                    <Th>
                                      {t('default_value', 'Default value')}
                                    </Th>
                                    <Th>{t('read_only', 'Read only')}</Th>
                                    <Th>{t('required', 'Required')}</Th>
                                    <Th>{t('actions', 'Actions')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {values.customMetadataFields.map((cm, idx) =>
                                    !cm.isSelected ? null : (
                                      <Tr key={idx}>
                                        <Td>{cm.name}</Td>

                                        <Td>{cm.schema.type}</Td>

                                        <Td>
                                          <Field
                                            name={`customMetadataFields.${idx}.schema.defaultValue`}
                                          >
                                            {({ field, meta, form }) => (
                                              <FormControl
                                                isInvalid={!!meta.error}
                                              >
                                                <DefaultValueFormComponent
                                                  field={field}
                                                  form={form}
                                                  type={cm.schema.type}
                                                  idx={idx}
                                                  isDisabled={!hasPermission}
                                                />
                                                <FormErrorMessage>
                                                  {meta.error}
                                                </FormErrorMessage>
                                              </FormControl>
                                            )}
                                          </Field>
                                        </Td>

                                        <Td>
                                          {(cm.schema.defaultValue ===
                                            undefined ||
                                            cm.schema.defaultValue === null ||
                                            (typeof cm.schema.defaultValue ===
                                              'string' &&
                                              cm.schema.defaultValue.trim() ===
                                                '') ||
                                            (Array.isArray(
                                              cm.schema.defaultValue,
                                            ) &&
                                              cm.schema.defaultValue.length ===
                                                0)) &&
                                            cm.schema.readOnly &&
                                            void setFieldValue(
                                              `customMetadataFields.${idx}.schema.readOnly`,
                                              false,
                                            )}

                                          <Checkbox
                                            className="read-only-checkbox"
                                            isChecked={cm.schema.readOnly}
                                            onChange={e =>
                                              setFieldValue(
                                                `customMetadataFields.${idx}.schema.readOnly`,
                                                e.target.checked,
                                              )
                                            }
                                            isDisabled={
                                              !hasPermission ||
                                              cm.schema.defaultValue ===
                                                undefined ||
                                              cm.schema.defaultValue === null ||
                                              (typeof cm.schema.defaultValue ===
                                                'string' &&
                                                cm.schema.defaultValue.trim() ===
                                                  '') ||
                                              (Array.isArray(
                                                cm.schema.defaultValue,
                                              ) &&
                                                cm.schema.defaultValue
                                                  .length === 0)
                                            }
                                          />
                                        </Td>

                                        <Td>
                                          <Checkbox
                                            className="required-checkbox"
                                            isChecked={
                                              cm.schema.isValueRequired
                                            }
                                            onChange={e =>
                                              setFieldValue(
                                                `customMetadataFields.${idx}.schema.isValueRequired`,
                                                e.target.checked,
                                              )
                                            }
                                            isDisabled={!hasPermission}
                                          />
                                        </Td>

                                        <Td>
                                          <Popover
                                            placement="right"
                                            closeOnBlur={false}
                                          >
                                            {({ onClose }) => (
                                              <>
                                                <PopoverTrigger>
                                                  <IconButton
                                                    aria-label="Remove custom metadata field"
                                                    icon={<RiDeleteBinFill />}
                                                    size="md"
                                                    variant="ghost"
                                                    _hover={{
                                                      bg: 'transparent',
                                                    }}
                                                    minW="10px"
                                                    color="appBrick"
                                                    onClick={e =>
                                                      e.stopPropagation()
                                                    }
                                                  />
                                                </PopoverTrigger>

                                                <PopoverContent>
                                                  <PopoverArrow />
                                                  <PopoverHeader fontWeight="semibold">
                                                    {t(
                                                      'remove_cmf_header',
                                                      'Remove custom metadata field',
                                                    )}
                                                  </PopoverHeader>
                                                  <PopoverCloseButton />
                                                  <PopoverBody>
                                                    <Flex
                                                      direction="column"
                                                      gap="1em"
                                                    >
                                                      <Text>
                                                        {t(
                                                          'confirm_cmf_remove',
                                                          'Are you sure you want to remove this field?',
                                                        )}
                                                      </Text>
                                                      <Button
                                                        size="sm"
                                                        colorScheme="appBrick"
                                                        alignSelf="left"
                                                        onClick={() => {
                                                          confirmDelete(idx)
                                                          onClose()
                                                        }}
                                                      >
                                                        {t('confirm')}
                                                      </Button>
                                                    </Flex>
                                                  </PopoverBody>
                                                </PopoverContent>
                                              </>
                                            )}
                                          </Popover>
                                        </Td>
                                      </Tr>
                                    ),
                                  )}
                                  {values.customMetadataFields.every(
                                    c => !c.isSelected,
                                  ) && (
                                    <Tr>
                                      <Td colSpan={6} textAlign="center">
                                        <Text>
                                          {t(
                                            'no_custom_metadata',
                                            'No custom metadata fields',
                                          )}
                                        </Text>
                                      </Td>
                                    </Tr>
                                  )}
                                </Tbody>
                              </Table>
                            </>
                          )}
                        </FieldArray>
                      </Stack>
                      <SelectCMFModal
                        isOpen={isOpenSelectCMFModal}
                        onClose={onCloseSelectCMFModal}
                        cmfList={values.customMetadataFields}
                        currentSelection={selectionFlags}
                        onAdd={handleAddCmfs}
                      />
                    </MainContentWrapper>
                    {isOpenDeletePathPolicy && (
                      <DeletePathPolicyModal
                        isOpenDeletePathPolicy={isOpenDeletePathPolicy}
                        onCloseDeletePathPolicy={onCloseDeletePathPolicy}
                        onSubmit={deletePathPolicy}
                        name={values.name}
                      />
                    )}
                  </Form>
                )
              }}
            </Formik>
          ) : (
            <Center width="full" height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          )}
        </>
      )}
    </PermissionGuard>
  )
}

export default PathPolicyForm
